import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import { FieldError } from "react-hook-form";
import { Operator } from "../model/Operator";
import { getOperators } from "../repositories/OperatorsRepository";

type Props = {
  label?: string;
  value: number | null;
  assetId: number;
  onChange: (value: number | null, name?: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  fieldError?: FieldError | undefined;
  disabled?: boolean;
  onlyForemans?: boolean;
  showAllOption?: boolean;
  disableClearable?: boolean;
};

export const OperatorSelector = ({
  label = "Operator",
  value,
  assetId,
  onChange = () => {},
  onBlur = () => {},
  onFocus = () => {},
  fieldError,
  disabled = false,
  onlyForemans = false,
  showAllOption = false,
  disableClearable = false,
}: Props) => {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [selectedOperator, setSelectedOperator] = useState<Operator | null>(
    showAllOption
      ? ({ OperatorId: null, OperatorName: "All" } as Operator)
      : null,
  );
  const filteredOperatorsRef = useRef<Operator[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    try {
      const resp = await getOperators(assetId, onlyForemans);
      if (resp.ok) {
        const rows = get(resp, "data.tables[0].rows", []) as Operator[];
        let updatedOperators: Operator[] = [...rows];

        if (showAllOption) {
          updatedOperators = [
            { OperatorId: null, OperatorName: "All" } as Operator,
            ...rows,
          ];
        }
        updatedOperators.sort((a, b) =>
          a.OperatorName.localeCompare(b.OperatorName),
        );
        setOperators(updatedOperators);

        if (value !== null && value !== undefined) {
          const initialOperator = updatedOperators.find(
            (operator) => operator.OperatorId === value,
          );
          setSelectedOperator(
            initialOperator ||
              (showAllOption
                ? ({ OperatorId: null, OperatorName: "All" } as Operator)
                : null),
          );
        } else {
          setSelectedOperator(
            showAllOption
              ? ({ OperatorId: null, OperatorName: "All" } as Operator)
              : null,
          );
        }
      } else {
        setError(resp.message);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while loading operators",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();

      if (filteredOperatorsRef.current.length === 1) {
        const selectedOption = filteredOperatorsRef.current[0];
        setSelectedOperator(selectedOption);
        onChange(selectedOption.OperatorId, selectedOption.OperatorName);

        inputRef.current?.blur();
      }
    }
  };

  useEffect(() => {
    if (operators.length > 0) {
      if (value !== null && value !== undefined) {
        const operator = operators.find((op) => op.OperatorId === value);
        setSelectedOperator(
          operator ||
            (showAllOption
              ? ({ OperatorId: null, OperatorName: "All" } as Operator)
              : null),
        );
      } else {
        setSelectedOperator(
          showAllOption
            ? ({ OperatorId: null, OperatorName: "All" } as Operator)
            : null,
        );
      }
    }
  }, [value, operators]);

  useEffect(() => {
    loadData();
  }, [assetId]);

  return (
    <>
      <Autocomplete
        id="operator-selector"
        disableClearable={disableClearable}
        clearOnEscape={!disableClearable}
        options={operators || []}
        getOptionLabel={(option) => option.OperatorName}
        filterOptions={(options, state) => {
          const filtered = options.filter((option) =>
            option.OperatorName.toLowerCase().includes(
              state.inputValue.toLowerCase(),
            ),
          );
          filteredOperatorsRef.current = filtered;
          return filtered;
        }}
        disabled={disabled}
        loading={isLoading}
        value={selectedOperator}
        onChange={(event, newValue) => {
          setSelectedOperator(newValue);
          onChange(newValue?.OperatorId ?? null, newValue?.OperatorName);
        }}
        renderOption={(props, option, state) => (
          <li {...props}>
            <span style={{ fontWeight: state.selected ? "bold" : "normal" }}>
              {option.OperatorName}
            </span>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            label={label}
            variant="outlined"
            fullWidth
            error={!!fieldError}
            helperText={fieldError?.message}
            onKeyDown={handleKeyDown}
            slotProps={{
              inputLabel: {
                sx: { fontSize: "clamp(.7rem,.8rem,1rem)" },
              },
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : (
                      params.InputProps.endAdornment
                    )}
                  </>
                ),
              },
            }}
            onBlur={onBlur}
            onFocus={onFocus}
          />
        )}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error loading Operators"
      />
    </>
  );
};
